import { Download } from 'lucide-react';
import dayjs from 'dayjs';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import html2pdf from 'html2pdf.js';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { EscrowStatusEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';
import { Certificate, type ICertificateType } from '@/widgets/Certificate';
import env from 'env';

import { AssetsTable, ContentTypeCountTable } from './ui';

type Props = {
  dialogTrigger: React.ReactNode;
  type: ICertificateType;
  canDownload?: boolean;
};

const certificateTypeMap = {
  validated: {
    scope:
      'The depositor has submitted and labelled assets for escrow. Codekeeper has confirmed receipt and secure storage of those assets in the Codekeeper vault. The accuracy and completeness of the deposited content remains the responsibility of the depositor.',
    note: 'This certificate documents the escrow arrangement and confirmed asset deposits as at the date of issue.\nIt does not constitute a verification of asset integrity, code content, or operational performance.',
  },
  verified: {
    scope:
      'The depositor has submitted and labelled assets for escrow. Codekeeper has confirmed receipt and secure storage of those assets in the Codekeeper vault, and carried out automated verification checks on those assets. Detailed verification results are available in the Reports section of the escrow.',
    note: 'This certificate documents the escrow arrangement and confirmed asset deposits as at the date of issue.\nIt does not constitute a verification of code content or operational performance.',
  },
  certified: {
    scope:
      'The depositor has submitted and labelled assets for escrow. Codekeeper has confirmed receipt and secure storage of those assets in the Codekeeper vault, and assessed the recovery-readiness of those assets. A detailed report has been shared with relevant stakeholders and may be requested from service@codekeeper.co.',
    note: 'This certificate documents the escrow arrangement and confirmed asset deposits as at the date of issue.\nIt does not constitute a guarantee of uninterrupted software availability or operational performance.',
  },
} as const;

const Content = () => {
  const {
    softwareEscrow: { status, beneficiarySharedInformation, currentCompanyRole },
  } = useSoftwareEscrow();

  if (status === EscrowStatusEnum.Terminated)
    return (
      <Certificate.TerminatedBlock
        heading="This escrow arrangement has been terminated."
        text="The assets associated with this arrangement are no longer in escrow. This certificate remains a record of the escrow that was in effect until termination."
      />
    );

  if (!beneficiarySharedInformation && currentCompanyRole === EscrowPartyTypeEnum.Beneficiary)
    return <ContentTypeCountTable />;

  return <AssetsTable />;
};

const CertificateLayout = memo(
  ({ type, ref }: { type: ICertificateType; ref: React.RefObject<HTMLHtmlElement | null> }) => {
    const {
      softwareEscrow: { depositor, beneficiary, status },
    } = useSoftwareEscrow();

    const { scope, note } = certificateTypeMap[type];

    const detailsItems = [
      {
        label: 'Depositor',
        value: depositor?.companyName ?? '',
      },
      {
        label: 'Beneficiary',
        value: beneficiary?.companyName ?? '',
      },
      {
        label: 'Arrangement type',
        value: 'Software Escrow',
      },
    ];

    return (
      <Certificate.Layout
        ref={ref}
        type={type}
        title="Escrow Certificate"
        description={
          <>
            This certificate, issued by Codekeeper as the escrow agent on {dayjs().format('MMM DD, YYYY')}, confirms the
            establishment of the following escrow arrangement and Codekeeper&apos;s receipt of the deposited assets:
          </>
        }
        isTerminated={status === EscrowStatusEnum.Terminated}
      >
        <Certificate.Details type={type} items={detailsItems} />
        <Certificate.TextBlock heading="Scope" text={scope} />
        <div className="mb-1.5 space-y-2 relative">
          <h6 className="text-2xs">Asset types in escrow</h6>
          <Content />
        </div>
        <Certificate.TextBlock
          heading="Release instructions"
          text={
            <>
              Should a verified release condition occur, the beneficiary must contact{' '}
              <a href={`mailto:${env.CODEKEEPER_SERVICE_EMAIL}`} className="hover:underline">
                {env.CODEKEEPER_SERVICE_EMAIL}
              </a>{' '}
              to initiate the release process.
            </>
          }
        />
        <Certificate.NoteText>{note}</Certificate.NoteText>
      </Certificate.Layout>
    );
  },
);
CertificateLayout.displayName = 'CertificateLayout';

const options = {
  print: async (printIframe: HTMLIFrameElement) => {
    const document = printIframe.contentDocument;
    if (document) {
      const html = document.getElementsByTagName('html')[0];
      const body = document.getElementsByTagName('body')[0];
      const container = body.children[0] as HTMLElement;
      // 1243px height of printIframe, 122px difference to get the correct height of the a4 page
      const iframeDefaultHeight = 1121;
      html.style.height = Math.ceil(body.clientHeight / iframeDefaultHeight) * iframeDefaultHeight + 'px';
      body.style.height = '100%';
      container.style.height = '100%';
      container.style.width = '100%';

      try {
        await html2pdf()
          .set({
            image: {
              type: 'jpeg',
              quality: 1.98,
            },
            jsPDF: { unit: 'pt', orientation: 'portrait', format: 'a4' },
            html2canvas: { scale: 2 },
            pagebreak: { mode: 'legacy' },
            filename: 'certificate.pdf',
          })
          .from(html)
          .save();
      } catch (error) {
        console.error(error);
      }
    }
  },
  pageStyle: 'img { display: unset; }',
};

const CertificateContainer = ({ dialogTrigger, type, canDownload = true }: Props) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const contentRef = useRef<HTMLHtmlElement>(null);
  const promiseResolveRef = useRef<((value: void | PromiseLike<void>) => void) | null>(null);

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) promiseResolveRef.current();
  }, [isPrinting]);

  const onBeforePrint = () => {
    return new Promise<void>((resolve) => {
      promiseResolveRef.current = resolve;
      setIsPrinting(true);
    });
  };

  const onAfterPrint = useCallback(() => {
    promiseResolveRef.current = null;
    setIsPrinting(false);
  }, []);

  const onDownload = useReactToPrint({
    contentRef,
    onBeforePrint,
    onAfterPrint,
    ...options,
    suppressErrors: true,
  });

  return (
    <Dialog>
      <DialogTrigger asChild>{dialogTrigger}</DialogTrigger>
      <DialogContent
        className="grid-rows-[auto_1fr_auto] sm:max-w-[645px] max-h-screen min-h-100 md:h-[calc(900px-80px)] px-0 gap-0 md:my-10"
        forceMount
      >
        <DialogHeader className="px-6 pb-4 border-b">
          <DialogTitle>Certificate</DialogTitle>
          <DialogDescription className="sr-only">Escrow Certificate</DialogDescription>
        </DialogHeader>
        <div className="grow overflow-y-auto bg-muted p-6">
          <CertificateLayout type={type} ref={contentRef} />
        </div>
        {canDownload && (
          <DialogFooter className="sm:justify-end border-t px-6 pt-4">
            <Button isLoading={isPrinting} onClick={onDownload} size="sm">
              <Download /> Download
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default memo(CertificateContainer);
